import { useState } from "react";
import { api, unwrap } from "../api/client";
import type { OutputView, StateMessage } from "../api/types";
import { ListenerModal } from "../components/ListenerModal";
import { LogsModal } from "../components/LogsModal";
import { OutputForm } from "../components/OutputForm";
import { OverlayModal } from "../components/OverlayModal";
import { OutputStatePill } from "../components/status";
import { Button, ConfirmDialog, EmptyState, PageHeader, Pill, Tag, useToast } from "../components/ui";
import { useCan, useClientLimits } from "../lib/auth";
import { formatBitrate, formatBytes, formatDuration, formatNumber, outputProtocolLabel } from "../lib/format";
import { serverText, t } from "../lib/i18n";
import { href } from "../lib/router";

/** Masque la fin d'une URL de destination (clé de stream YouTube, etc.). */
function maskUrl(url: string): string {
  const m = url.match(/^(rtmps?:\/\/[^/]+\/[^/]+\/)(.+)$/);
  return m ? `${m[1]}${"•".repeat(Math.min(12, m[2]!.length))}` : url;
}

export function Outputs({ state }: { state: StateMessage }) {
  const toast = useToast();
  const can = useCan();
  const isClient = useClientLimits() !== null;
  const [editing, setEditing] = useState<OutputView | "new" | null>(null);
  const [deleting, setDeleting] = useState<OutputView | null>(null);
  const [stopping, setStopping] = useState<OutputView | null>(null);
  const [logs, setLogs] = useState<OutputView | null>(null);
  const [access, setAccess] = useState<number | null>(null);      // id : la fenêtre suit l'état en direct
  const accessOutput = state.outputs.find((o) => o.id === access);
  const [dressing, setDressing] = useState<number | null>(null);
  const dressedOutput = state.outputs.find((o) => o.id === dressing);
  const [busy, setBusy] = useState<number | null>(null);
  const inputName = (id: string) => state.inputs.find((i) => i.id === id)?.name ?? id;

  const run = async (o: OutputView, action: "start" | "stop") => {
    setBusy(o.id);
    try {
      const path = action === "start" ? "/api/outputs/{output_id}/start" : "/api/outputs/{output_id}/stop";
      await unwrap(api.POST(path, { params: { path: { output_id: o.id } } }));
      toast(action === "start" ? t("Sortie « {name} » démarrée", { name: o.name }) : t("Sortie « {name} » arrêtée", { name: o.name }),
        action === "start" ? "ok" : "warn");
    } catch (e) {
      toast((e as Error).message, "err");
    } finally {
      setBusy(null);
      setStopping(null);
    }
  };

  const remove = async () => {
    if (!deleting) return;
    try {
      await unwrap(api.DELETE("/api/outputs/{output_id}", { params: { path: { output_id: deleting.id } } }));
      toast(t("Sortie « {name} » supprimée", { name: deleting.name }));
    } catch (e) {
      toast((e as Error).message, "err");
    }
    setDeleting(null);
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader title={t("Sorties")} subtitle={t("Destinations alimentées par la gateway")}
        actions={can("operator") && <Button variant="primary" onClick={() => setEditing("new")} disabled={state.inputs.length === 0}>{t("Nouvelle sortie")}</Button>} />

      {state.outputs.length === 0 ? (
        <EmptyState title={t("Aucune sortie")}
          action={can("operator") && state.inputs.length > 0 && <Button variant="primary" onClick={() => setEditing("new")}>{t("Créer une sortie")}</Button>}>
          {state.inputs.length === 0
            ? <>{t("Une sortie renvoie une entrée vers une destination.")} <a className="text-accent hover:underline" href={href({ page: "inputs" })}>{t("Créez d'abord une entrée.")}</a></>
            : t("Une sortie renvoie une entrée vers une destination : CDN en SRT, YouTube en RTMP, multicast UDP…")}
        </EmptyState>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-line bg-surface">
          <table className="w-full min-w-[980px] text-left">
            <thead className="border-b border-line font-cond text-[12px] uppercase tracking-wider text-faint">
              <tr>
                <th className="px-4 py-2 font-semibold">{t("État")}</th>
                <th className="px-4 py-2 font-semibold">{t("Sortie")}</th>
                <th className="px-4 py-2 font-semibold">{t("Destination")}</th>
                <th className="px-4 py-2 text-right font-semibold">{t("Débit")}</th>
                <th className="px-4 py-2 text-right font-semibold">{t("Durée")}</th>
                <th className="px-4 py-2 text-right font-semibold">{t("Relances")}</th>
                <th className="px-4 py-2 text-right font-semibold" title={t("Processeur (% du PC) et mémoire de la sortie")}>{t("Processeur")}</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {state.outputs.map((o) => {
                const s = o.status;
                return (
                  <tr key={o.id} className="align-top">
                    <td className="px-4 py-2.5"><OutputStatePill state={s.state} /></td>
                    <td className="px-4 py-2.5">
                      <div className="font-medium">{o.name}</div>
                      {o.owner && !isClient && <div className="text-[12px] text-faint">{t("client {name}", { name: o.owner })}</div>}
                      {s.on_backup && <div className="my-1"><Pill tone="warn">{t("Sur le secours")}</Pill></div>}
                      <div className="text-[12px] text-muted">
                        {t("source")} <a className={s.on_backup ? "line-through hover:text-accent" : "hover:text-accent"}
                          href={href({ page: "input", id: o.input_id })}>{inputName(o.input_id)}</a>
                        {o.backup_input_id && <> · {t("secours")} <a className={s.on_backup ? "text-warn hover:text-accent" : "hover:text-accent"}
                          href={href({ page: "input", id: o.backup_input_id })}>{inputName(o.backup_input_id)}</a></>}
                        {o.audio !== "none" && o.audio_tracks && <> · {o.audio_tracks.length > 1 ? t("pistes") : t("piste")} {o.audio_tracks.map((n) => n + 1).join(", ")}</>}
                        {(o.video !== "copy" || o.audio !== "copy") && <> · {t("transcodage")} {o.video === "h264" ? `H.264 ${o.video_bitrate_kbps} kb/s` : ""} {o.audio !== "copy" ? `audio ${o.audio}` : ""}</>}
                      </div>
                      {s.state === "retrying" && s.last_error && <div className="mt-1 max-w-md text-[12px] text-err">{serverText(s.last_error)}</div>}
                    </td>
                    <td className="max-w-[320px] px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <Tag>{outputProtocolLabel(o.protocol)}</Tag>
                        {o.protocol === "ndi"
                          ? <span className="truncate text-[12px] text-muted">{o.ndi_name || o.name} · {o.ndi_format}</span>
                          : o.listener
                          ? <span className="text-[12px] text-muted">
                              {s.clients.length === 0 ? t("aucun client") : s.clients.length === 1 ? t("1 client") : t("{n} clients", { n: s.clients.length })}
                              {s.clients.length > 0 && <> · {s.clients.map((c) => c.address?.split(":")[0]).join(", ")}</>}
                            </span>
                          : <span className="truncate font-mono text-[12px] text-muted" title={maskUrl(o.url)}>{maskUrl(o.url)}</span>}
                      </div>
                      {o.listener && <button type="button" className="mt-1 text-[12px] text-accent hover:underline"
                        onClick={() => setAccess(o.id)}>{t("Accès client")}</button>}
                    </td>
                    <td className="num px-4 py-2.5 text-right whitespace-nowrap">
                      {formatBitrate(s.bitrate_kbps)}
                      {s.bitrate_estimated && s.state === "running" && <div className="text-[11px] text-faint">{t("estimé")}</div>}
                    </td>
                    <td className="num px-4 py-2.5 text-right whitespace-nowrap">{s.state === "running" ? formatDuration(s.uptime_s ?? 0) : "–"}</td>
                    <td className={`num px-4 py-2.5 text-right ${(s.restarts ?? 0) > 0 ? "text-warn" : ""}`}>{s.restarts ?? 0}</td>
                    <td className="num px-4 py-2.5 text-right whitespace-nowrap">
                      {s.cpu_percent != null ? `${formatNumber(s.cpu_percent)} %` : "–"}
                      {s.memory_bytes != null && <div className="text-[11px] text-faint">{formatBytes(s.memory_bytes)}</div>}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex justify-end gap-1">
                        {can("operator") && <>
                          {o.enabled
                            ? <Button size="sm" disabled={busy === o.id} onClick={() => setStopping(o)}>{t("Arrêter")}</Button>
                            : <Button size="sm" variant="primary" disabled={busy === o.id} onClick={() => run(o, "start")}>{t("Démarrer")}</Button>}
                          {o.overlay && <Button size="sm" variant="ghost" onClick={() => setDressing(o.id)}>{t("Habillage")}</Button>}
                          <Button size="sm" variant="ghost" onClick={() => setLogs(o)}>{t("Logs")}</Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditing(o)}>{t("Modifier")}</Button>
                        </>}
                        {can("admin") && <Button size="sm" variant="ghost" onClick={() => setDeleting(o)}>{t("Supprimer")}</Button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <OutputForm output={editing === "new" ? undefined : editing} inputs={state.inputs} onClose={() => setEditing(null)}
          onSaved={(saved) => {
            toast(editing === "new" ? t("Sortie « {name} » créée", { name: saved.name }) : t("Sortie « {name} » modifiée et relancée", { name: saved.name }));
            setEditing(null);
          }} />
      )}
      {logs && <LogsModal output={logs} onClose={() => setLogs(null)} />}
      {accessOutput && <ListenerModal output={accessOutput} onClose={() => setAccess(null)} />}
      {dressedOutput && <OverlayModal key={dressedOutput.id} output={dressedOutput} onClose={() => setDressing(null)} />}
      <ConfirmDialog open={stopping !== null} title={t("Arrêter la sortie ?")} confirmLabel={t("Arrêter")}
        onCancel={() => setStopping(null)} onConfirm={() => stopping && run(stopping, "stop")}>
        {t("La diffusion vers « {name} » sera coupée. Elle ne redémarrera pas seule, même après un redémarrage du serveur.", { name: stopping?.name ?? "" })}
      </ConfirmDialog>
      <ConfirmDialog open={deleting !== null} title={t("Supprimer la sortie ?")} confirmLabel={t("Supprimer")}
        onCancel={() => setDeleting(null)} onConfirm={remove}>
        {t("« {name} » sera arrêtée et supprimée. Cette action est définitive.", { name: deleting?.name ?? "" })}
      </ConfirmDialog>
    </div>
  );
}
